import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "../hooks/useAuth0";
import {
  Box,
  Container,
  Typography,
  Button,
  Divider,
  CircularProgress,
  TextField,
  Alert,
  InputAdornment,
  IconButton,
  useMediaQuery,
  useTheme,
  Fade,
  Slide,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import SpeedIcon from "@mui/icons-material/Speed";
import SecurityIcon from "@mui/icons-material/Security";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

// Official brand-compliant logo marks — same as Login.jsx. Duplicated here
// rather than shared, since these two files may get consolidated later
// (see note at the bottom of this file) and keeping them self-contained
// avoids a premature shared-component split.
function GoogleLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.964 10.706A5.41 5.41 0 0 1 3.68 9c0-.593.102-1.17.284-1.706V4.962H.957A9.001 9.001 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" />
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58z" />
    </svg>
  );
}

function AppleLogo() {
  return (
    <svg width="17" height="21" viewBox="0 0 170 210" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        fill="#FFFFFF"
        d="M150.4 112.9c-.3-27.1 22.1-40.1 23.1-40.7-12.6-18.4-32.2-20.9-39.2-21.2-16.7-1.7-32.6 9.8-41.1 9.8-8.5 0-21.6-9.6-35.6-9.3-18.3.3-35.2 10.6-44.6 27-19.1 33-4.9 81.9 13.7 108.7 9.1 13.1 20 27.8 34.3 27.3 13.8-.6 19-8.9 35.7-8.9 16.6 0 21.3 8.9 35.8 8.6 14.8-.2 24.2-13.2 33.2-26.4 10.5-15.1 14.8-29.7 15-30.5-.3-.1-28.8-11.1-29.1-44.4z"
      />
      <path
        fill="#FFFFFF"
        d="M124.9 33.4c7.5-9.1 12.6-21.7 11.2-34.4-10.8.4-24 7.2-31.7 16.1-6.9 7.9-13 20.7-11.4 33 12 .9 24.4-6.1 31.9-14.7z"
      />
    </svg>
  );
}

const Auth = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const {
    isAuthenticated,
    isLoading,
    signInWithEmail,
    signInWithApple,
    signInWithGoogle,
  } = useAuth0();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // NOTE: this page previously also handled an in-page "signup mode"
  // (toggled by checking location.pathname === "/signup", with role
  // selection etc.), but that branch was actually unreachable — /signup
  // is routed to the separate Signup.jsx wizard, which is the real signup
  // flow and already posts to the backend. This page is login-only now.

  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, isLoading, navigate]);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signInWithEmail(email, password);
      navigate("/dashboard");
    } catch (err) {
      console.error("Login error:", err);
      setError(
        err?.message?.includes("Incorrect username or password")
          ? "Incorrect email or password."
          : err?.message || "Sign in failed. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
        }}
      >
        <CircularProgress size={60} sx={{ color: "white" }} />
      </Box>
    );
  }

  const benefits = [
    {
      icon: <SpeedIcon sx={{ fontSize: 40 }} />,
      title: "Lightning Fast",
      description: "Match patients to trials in seconds with AI",
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 40 }} />,
      title: "HIPAA Compliant",
      description: "Bank-level security for patient data",
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
      title: "300% Better Results",
      description: "Increase enrollment rates dramatically",
    },
  ];

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", background: "#f5f7fa" }}>
      {/* Left Side - Hero Section */}
      {!isMobile && (
        <Fade in timeout={800}>
          <Box
            sx={{
              flex: 1,
              background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
              color: "white",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              p: 8,
              position: "relative",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                width: "500px",
                height: "500px",
                borderRadius: "50%",
                background: "rgba(255,255,255,0.1)",
                top: "-200px",
                right: "-200px",
              }}
            />

            <Box sx={{ display: "flex", alignItems: "center", mb: 6, position: "relative", zIndex: 1 }}>
              <LocalHospitalIcon sx={{ fontSize: 50, mr: 2 }} />
              <Typography variant="h4" fontWeight="bold">
                NephroTrax
              </Typography>
            </Box>

            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Typography variant="h2" fontWeight="bold" sx={{ mb: 3, lineHeight: 1.2 }}>
                Welcome back to NephroTrax
              </Typography>
              <Typography variant="h6" sx={{ mb: 6, opacity: 0.9, lineHeight: 1.6 }}>
                Continue your journey in revolutionizing clinical trial matching
              </Typography>

              <Box sx={{ mb: 6 }}>
                {benefits.map((benefit, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 3,
                      p: 2,
                      borderRadius: 2,
                      background: "rgba(255,255,255,0.1)",
                    }}
                  >
                    <Box sx={{ mr: 2 }}>{benefit.icon}</Box>
                    <Box>
                      <Typography variant="h6" fontWeight="bold">
                        {benefit.title}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        {benefit.description}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>

              <Box sx={{ display: "flex", gap: 4, mt: 6 }}>
                <Box>
                  <Typography variant="h3" fontWeight="bold">10K+</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>Clinical Trials</Typography>
                </Box>
                <Box>
                  <Typography variant="h3" fontWeight="bold">5K+</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>Providers</Typography>
                </Box>
                <Box>
                  <Typography variant="h3" fontWeight="bold">98%</Typography>
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>Satisfaction</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Fade>
      )}

      {/* Right Side - Auth Form */}
      <Box
        sx={{
          flex: isMobile ? 1 : "0 0 480px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
          background: "#ffffff",
        }}
      >
        <Slide direction="left" in timeout={500}>
          <Box sx={{ width: "100%", maxWidth: "400px" }}>
            {isMobile && (
              <Box sx={{ textAlign: "center", mb: 4 }}>
                <LocalHospitalIcon sx={{ fontSize: 50, color: "#1A5EA8" }} />
                <Typography variant="h5" fontWeight="bold" sx={{ mt: 1 }}>
                  NephroTrax
                </Typography>
              </Box>
            )}

            <Box sx={{ mb: 4 }}>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Sign in to your account
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Welcome back! Please enter your details.
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* Email / Password form */}
            <Box component="form" onSubmit={handleEmailLogin} sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                sx={{ mb: 1 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword((v) => !v)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ textAlign: "right", mb: 2 }}>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => navigate("/forgot-password")}
                  sx={{ textTransform: "none" }}
                >
                  Forgot password?
                </Button>
              </Box>
              <Button
                fullWidth
                type="submit"
                variant="contained"
                size="large"
                disabled={submitting}
                sx={{
                  py: 1.5,
                  textTransform: "none",
                  fontSize: "1rem",
                  fontWeight: 600,
                  background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)",
                  },
                }}
              >
                {submitting ? <CircularProgress size={24} color="inherit" /> : "Sign In"}
              </Button>
            </Box>

            <Divider sx={{ my: 3 }}>
              <Typography variant="body2" color="text.secondary">OR</Typography>
            </Divider>

            {/* Social sign-in */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 3 }}>
              <Button
                type="button"
                fullWidth
                variant="outlined"
                size="large"
                onClick={signInWithGoogle}
                startIcon={<GoogleLogo />}
                sx={{
                  py: 1.5,
                  textTransform: "none",
                  fontSize: "0.95rem",
                  fontWeight: 500,
                  borderColor: "#dadce0",
                  backgroundColor: "#ffffff",
                  color: "#3c4043",
                  borderRadius: "4px",
                  "&:hover": {
                    borderColor: "#d2d3d4",
                    backgroundColor: "#f8f9fa",
                    boxShadow: "0 1px 2px 0 rgba(60,64,67,0.3), 0 1px 3px 1px rgba(60,64,67,0.15)",
                  },
                }}
              >
                Continue with Google
              </Button>

              <Button
                type="button"
                fullWidth
                variant="contained"
                size="large"
                onClick={signInWithApple}
                startIcon={<AppleLogo />}
                sx={{
                  py: 1.5,
                  textTransform: "none",
                  fontSize: "0.95rem",
                  fontWeight: 500,
                  backgroundColor: "#000000",
                  color: "#ffffff",
                  borderRadius: "4px",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#1a1a1a",
                    boxShadow: "none",
                  },
                }}
              >
                Continue with Apple
              </Button>
            </Box>

            {/* Signup Link — the real signup flow is the Signup.jsx wizard at /signup */}
            <Box sx={{ textAlign: "center", mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Don't have an account?{" "}
                <Button
                  type="button"
                  variant="text"
                  onClick={() => navigate("/signup")}
                  sx={{ textTransform: "none", fontWeight: "bold", p: 0 }}
                >
                  Sign up for free
                </Button>
              </Typography>
            </Box>

            <Box sx={{ mt: 4, pt: 3, borderTop: "1px solid #e0e0e0", textAlign: "center" }}>
              <Typography variant="caption" color="text.secondary">
                🔒 256-bit SSL • HIPAA Compliant • SOC 2 Certified
              </Typography>
            </Box>
          </Box>
        </Slide>
      </Box>
    </Box>
  );
};

export default Auth;